import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { onPortalHost } from '../utils/portalAddress'

const api = axios.create({
    baseURL: '/api/v1',
    headers: { Accept: 'application/json' },
    withCredentials: true,
    withXSRFToken: true,
})

api.interceptors.request.use(config => {
    const companyId = localStorage.getItem('cortex_company_id')
    if (companyId) config.headers['X-Company-Id'] = companyId
    if (window.__cortexDev?.()) config.headers['X-Dev-Debug'] = '1'
    return config
})

api.interceptors.response.use(
    response => response,
    error => {
        const status = error.response?.status

        if (status === 401) {
            const path = window.location.pathname
            // On the portal's own host every page is the portal's, and its sign-in is /login.
            const portalHost = onPortalHost()
            const isPortal = portalHost || path.startsWith('/portal')
            const alreadyOnLoginPage = path === '/login' || path.startsWith('/login/') || path === '/portal/login'
            if (!alreadyOnLoginPage) {
                // Carry the address, so a shared link - a ticket, one comment on it - still
                // arrives after signing in. Without it this reload threw the address away
                // even when the router had already kept it.
                const here = path + window.location.search + window.location.hash
                const back = here !== '/' && here !== '/portal' ? `?redirect=${encodeURIComponent(here)}` : ''
                window.location.href = (isPortal && !portalHost ? '/portal/login' : '/login') + back
            }
        }

        // Capture server errors — these represent bugs, not expected application errors.
        // 4xx (validation, not found, auth) are intentional and stay silent.
        if (status >= 500) {
            Sentry.captureException(error, {
                tags: {
                    kind: 'api_error',
                    http_status: status,
                    http_method: error.config?.method?.toUpperCase(),
                },
                extra: {
                    url: error.config?.url,
                    request_data: error.config?.data,
                    response_body: typeof error.response?.data === 'string'
                        ? error.response.data.slice(0, 500)
                        : JSON.stringify(error.response?.data ?? {}).slice(0, 500),
                },
            })
        }

        return Promise.reject(error)
    }
)

export default api

// ── Dev panel interceptors (staging / dev only) ───────────────────────────────
import { attachDevInterceptors, installGlobalDevLog, attachFetchInterceptor, patchAxiosForStack } from './devInterceptors.js'

// null = auth not yet loaded (fall back to hostname)
// true = explicitly ON  (DB toggle / auth)
// false = explicitly OFF (DB toggle — overrides hostname)
window.__devPanelEnabled = null
window.__cortexDev = () => {
    if (window.__devPanelEnabled === false) return false   // explicit off beats everything
    if (window.__devPanelEnabled === true)  return true    // explicit on beats everything
    // not yet decided — use hostname/localStorage
    return window.location.hostname.includes('dev-ignite-os') ||
           window.location.hostname === 'localhost' ||
           localStorage.getItem('cortex_dev') === '1'
}

attachDevInterceptors(api)
patchAxiosForStack(api)
installGlobalDevLog()
attachFetchInterceptor()
